import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AlphaFab from "@/components/AlphaFab";
import mascot from "@/assets/alpha-mascot.png";
import {
  LayoutDashboard, Wrench, Users as UsersIcon, ShieldCheck, Settings as Cog,
  Store, LogOut, ChevronDown, Menu, X, PanelLeftClose, PanelLeftOpen, Package, Map,
  Smartphone, Contact, BarChart3, HeartHandshake, Receipt, LineChart,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", end: true },
  { to: "/app/os", label: "Ordens de Serviço", icon: Wrench, perm: "service_orders.view" },
  { to: "/app/pdv", label: "PDV / Caixa", icon: Store, perm: "pdv.view" },
  { to: "/app/estoque", label: "Estoque", icon: Package, perm: "inventory.view" },
  { to: "/app/aparelhos", label: "Entrada de Aparelhos", icon: Smartphone, perm: "device_purchase.view" },
  { to: "/app/celulares-boleto", label: "Celular no Boleto", icon: Receipt, perm: "device_sale.view" },
  { to: "/app/clientes", label: "Clientes", icon: Contact, perm: "clients.view" },
  { to: "/app/pos-venda", label: "Pós-venda", icon: HeartHandshake, perm: "pos_sale.view" },
  { to: "/app/mapa", label: "Mapa Alpha", icon: Map, perm: "clients.view" },
  { to: "/app/relatorios", label: "Relatórios", icon: BarChart3, perm: "reports.view" },
  { to: "/app/evolucao-financeira", label: "Evolução Financeira", icon: LineChart, perm: "reports.view" },
  { to: "/app/loja", label: "Loja Virtual", icon: Store, perm: "loja_virtual.view" },
  { to: "/app/usuarios", label: "Usuários", icon: UsersIcon, perm: "users.view" },
  { to: "/app/cargos", label: "Cargos & RBAC", icon: ShieldCheck, perm: "roles.view" },
  { to: "/app/configuracoes", label: "Configurações", icon: Cog, perm: "settings.view" },
  { to: "/app/roadmap", label: "Roadmap", icon: Map, perm: "settings.view", adminOnly: true },
];

export default function Layout() {
  const { user, stores, switchStore, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeStore = stores.find((s) => s.id === user?.active_store_id);
  const items = nav.filter((n) => hasPerm(n.perm) && (!n.adminOnly || user?.role === "admin"));

  const SidebarInner = ({ showLabels }) => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-4 border-b border-white/5">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center font-display font-extrabold text-black shrink-0"
          style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>A</div>
        {showLabels && (
          <div className="leading-none">
            <div className="font-display font-extrabold text-white tracking-tight">Alpha<span className="alpha-gold-text">OS</span></div>
            <div className="text-[10px] text-zinc-500 tracking-widest">v1.3 · ALPHA PRIME</div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${n.to === "/app" ? "dashboard" : n.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-gold/15 text-gold-bright border border-gold/30" : "text-zinc-400 hover:text-white hover:bg-white/5"
              } ${showLabels ? "" : "justify-center"}`}
            title={n.label}>
            <n.icon size={18} className="shrink-0" /> {showLabels && n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5 hidden lg:block">
        <button data-testid="sidebar-collapse" onClick={() => setCollapsed((c) => !c)}
          className={`flex items-center gap-2 text-zinc-500 hover:text-white text-sm w-full rounded-lg px-2 py-2 hover:bg-white/5 ${showLabels ? "" : "justify-center"}`}>
          {collapsed ? <PanelLeftOpen size={18} /> : <><PanelLeftClose size={18} /> Recolher</>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex">
      {/* Desktop sidebar */}
      <aside data-testid="sidebar" className={`hidden lg:flex flex-col shrink-0 border-r border-white/5 bg-[#0C0C10] transition-all duration-200 ${collapsed ? "w-[76px]" : "w-64"}`}>
        <SidebarInner showLabels={!collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 z-50 bg-[#0C0C10] border-r border-white/10 lg:hidden">
            <button data-testid="mobile-close" onClick={() => setMobileOpen(false)} className="absolute top-4 right-3 text-zinc-400"><X size={20} /></button>
            <SidebarInner showLabels />
          </aside>
        </>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="alpha-glass sticky top-0 z-30 h-16 flex items-center gap-3 px-4 lg:px-6">
          <button data-testid="mobile-menu" onClick={() => setMobileOpen(true)} className="lg:hidden text-zinc-300"><Menu size={22} /></button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="store-switcher" className="flex items-center gap-2 rounded-full bg-[#1A1A22] border border-gold/20 px-3 py-1.5 text-sm text-zinc-200 hover:border-gold/40 transition-colors">
                <Store size={15} className="text-gold" />
                <span className="max-w-[160px] truncate">{activeStore?.name || "Selecionar loja"}</span>
                <ChevronDown size={14} className="text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121217] border-gold/20 text-white">
              <DropdownMenuLabel>Lojas</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {stores.map((s) => (
                <DropdownMenuItem key={s.id} data-testid={`store-option-${s.code}`} onClick={() => switchStore(s.id)} className="cursor-pointer focus:bg-gold/10">
                  {s.name} <span className="ml-2 text-xs text-zinc-500">{s.code}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-white/5 transition-colors">
                <div className="h-8 w-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold-bright font-semibold text-sm">
                  {(user?.name || "?").charAt(0)}
                </div>
                <div className="text-left leading-tight hidden sm:block">
                  <div className="text-sm text-white font-medium">{user?.name}</div>
                  <div className="text-[11px] text-zinc-500 capitalize">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121217] border-gold/20 text-white">
              <DropdownMenuLabel className="text-xs text-zinc-400">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer focus:bg-red-500/10 text-red-300">
                <LogOut size={15} className="mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
      <AlphaFab />
    </div>
  );
}
